import {
    ButtonPickerDate,
    Container,
    ContainerButtons,
    ContentButtons,
    ContentButtonsUp,
    DateRangePickerStyled,
    OutlinedFieldWrapper,
    OutlinedLabel,
} from "./styled";
import {RangeValue, SelectDatePropsType} from "@dropDesk/presentation/components/dates/select_range_date/select_date";
import React, {useEffect, useRef, useState} from "react";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {Brightness} from "@dropDesk/domain/entities/theme/colors.entity";
import {SharedColors} from "@dropDesk/domain/entities/theme/app_colors";
import {
    differenceBetweenDatesInDays,
    getCurrentDate,
    getUtcCalendarDaysDiff,
    setHourZero,
    setMaxHour
} from "@dropDesk/utils/helpers/date_helper";
import LabelInput from "@dropDesk/presentation/components/inputs/input_with_border/label";
import {getStaticDateFromPeriod, PeriodEnum} from "@dropDesk/utils/helpers/common";

const PRESET_LABELS: Partial<Record<PeriodEnum, string>> = {
    [PeriodEnum.now]: 'Hoje',
    [PeriodEnum.yesterday]: 'Ontem',
    [PeriodEnum.currentWeek]: 'Semana atual',
    [PeriodEnum.lastWeek]: 'Semana passada',
    [PeriodEnum.currentMonth]: 'Mês atual',
    [PeriodEnum.lastMonth]: 'Mês passado',
    [PeriodEnum.last3Months]: 'Últimos 3 meses',
    [PeriodEnum.last6Months]: 'Últimos 6 meses',
    [PeriodEnum.last12Months]: 'Últimos 12 meses',
};

const DEFAULT_PRESET_BUTTONS: PeriodEnum[] = [
    PeriodEnum.now,
    PeriodEnum.currentWeek,
    PeriodEnum.lastWeek,
    PeriodEnum.yesterday,
    PeriodEnum.currentMonth,
    PeriodEnum.lastMonth,
];

const SelectRangeDate = (props: SelectDatePropsType) => {

    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const inputRef = useRef<HTMLInputElement>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pendingValue, setPendingValue] = useState<RangeValue | null>(null);
    const borderColor = props.borderColor ?? colors.border;
    const presetButtons = props.presetButtons ?? DEFAULT_PRESET_BUTTONS;

    useEffect(() => {
        if (inputRef && inputRef.current) {
            inputRef.current.blur();
        }
    }, []);

    useEffect(() => {
        if (props.disabled) {
            setPickerOpen(false);
            setPendingValue(null);
        }
    }, [props.disabled]);

    const preventSelectDateLaterToday = (current: Date): boolean => {
        return (current.getTime() > setMaxHour(getCurrentDate()).getTime());
    }

    const preventSelectDateBeforeMinDate = (current: Date, minDateInDays: number): boolean => {
        return (differenceBetweenDatesInDays(current, new Date()) > minDateInDays);
    }

    const disabledDate = (current: Date): boolean => {
        if (props.minDateInDays) {
            return preventSelectDateBeforeMinDate(current, props.minDateInDays) || (preventSelectDateLaterToday(current));
        }

        return (preventSelectDateLaterToday(current));
    }

    const isRangeWithinMaxDays = (range: RangeValue | null): boolean => {
        if (!props.maxRangeDays || !range?.[0] || !range?.[1]) {
            return true;
        }
        return getUtcCalendarDaysDiff(range[0], range[1]) <= props.maxRangeDays;
    };

    const handleOnChange = (range: RangeValue | null, label: PeriodEnum) => {
        if (label === PeriodEnum.custom && !isRangeWithinMaxDays(range)) {
            props.onRangeRejected?.();
            return;
        }
        props.onChange(range, label);
    }

    const handlePresetSelect = (range: RangeValue, label: PeriodEnum) => {
        setPendingValue(null);
        handleOnChange(range, label);
        setPickerOpen(false);
    }

    const handleOpenChange = (open: boolean) => {
        if (props.disabled) {
            return;
        }
        setPickerOpen(open);
        if (open) {
            setPendingValue([null, null]);
            return;
        }
        setPendingValue(null);
    }

    const getLabelPtBr = (labelPeriod: PeriodEnum): string => {
        return PRESET_LABELS[labelPeriod] ?? 'Customizado';
    }

    const renderPresetButton = (period: PeriodEnum, width = 106) => {
        const label = PRESET_LABELS[period];
        if (!label) {
            return null;
        }
        const preset = getStaticDateFromPeriod(period);
        return (
            <ButtonPickerDate
                key={period}
                width={width}
                color={colors.text}
                border={colors.border}
                accent={colors.accent}
                background={colors.onBackground}
                onClick={() => {
                    handlePresetSelect(preset.range as RangeValue, preset.label);
                }}>
                {label}
            </ButtonPickerDate>
        );
    };

    const firstRowPresets = presetButtons.slice(0, 3);
    const secondRowPresets = presetButtons.slice(3, 6);
    const isOutlined = props.labelVariant === 'outlined';

    const dateRangePicker = (
        <DateRangePickerStyled
            border={colors.border}
            accent={colors.accent}
            ref={inputRef}
            disabled={props.disabled}
            autoFocus={props.autoFocus}
            open={pickerOpen}
            onOpenChange={handleOpenChange}
            onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) {
                    const start = setHourZero(new Date(dates[0].getTime()));
                    const end = setMaxHour(new Date(dates[1].getTime()));
                    setPendingValue(null);
                    setPickerOpen(false);
                    handleOnChange([start, end], PeriodEnum.custom);
                    return;
                }
                handleOnChange(dates, PeriodEnum.custom);
            }}
            value={pendingValue ?? props.value}
            hint={colors.hint}
            disabledDate={disabledDate}
            placeholder={props.placeholder}
            inputReadOnly={false}
            format={["d/M/y", 'dMy']}
            allowClear={props.allowClear ?? false}
            style={
                {
                    height: props.size,
                    width: '100%',
                    borderColor: isOutlined ? 'transparent' : borderColor,
                    backgroundColor: isOutlined ? 'transparent' : colors.backgroundInput,
                    borderRadius: 5,
                    fontSize: 14,
                    border: isOutlined ? 'none' : undefined,
                }}
            getPopupContainer={(trigger) => trigger}
            color={colors.text}
            placeholdercolor={colors.hint}
            focuscolor={colors.text}
            background={colors.backgroundInput}
            shadowcolor={colors.brightness === Brightness.light ? SharedColors.black : colors.border}
            backgroundmainbutton={colors.accent}
            renderExtraFooter={() => {
                return (
                    <ContainerButtons>
                        {firstRowPresets.length > 0 &&
                            <ContentButtonsUp>
                                {firstRowPresets.map((period) => renderPresetButton(period, period === PeriodEnum.now ? 46 : 106))}
                            </ContentButtonsUp>
                        }
                        {secondRowPresets.length > 0 &&
                            <ContentButtons>
                                {secondRowPresets.map((period) => renderPresetButton(period, period === PeriodEnum.yesterday ? 46 : 106))}
                            </ContentButtons>
                        }
                    </ContainerButtons>
                )
            }}
        />
    );

    return (
        <Container marginTop={props.marginTop ?? 0}>
            {!isOutlined && (!!props.label || !!props.labelPeriodCustom) &&
                <LabelInput required={props.required} label={props.label ?? getLabelPtBr(props.labelPeriodCustom ?? PeriodEnum.custom)}/>
            }

            {isOutlined && !!props.label ? (
                <OutlinedFieldWrapper
                    border={borderColor}
                    accent={colors.accent}
                    background={colors.backgroundInput}
                >
                    <OutlinedLabel color={colors.hint} background={colors.backgroundInput}>
                        {props.label}
                    </OutlinedLabel>
                    {dateRangePicker}
                </OutlinedFieldWrapper>
            ) : dateRangePicker}

        </Container>
    )
}
export default SelectRangeDate;
